import { inject, Injectable } from '@angular/core';
import { CostType, GeneralRegulatoryMode } from '@eadsysteme/billingclient-v1';
import { TranslocoService } from '@jsverse/transloco';

export interface VatRateOption {
	value: number;
	label: string;
	hint: string;
	modes: 'all' | GeneralRegulatoryMode[];
}

/**
 * Service for handling VAT rate calculations and configurations. Currently used in combination with costGroup configuration.
 */
@Injectable({ providedIn: 'root' })
export class VatRateService {
	private readonly translocoService = inject(TranslocoService);

	// Central VAT rate configuration per regulatory mode
	// To add a new country: add entry to VAT_RATES, VAT_RATE_OPTIONS_ALL and COST_TYPE_VAT_MAPPING
	readonly VAT_RATES: Record<GeneralRegulatoryMode, { zero: number; reduced: number; standard: number }> = {
		[GeneralRegulatoryMode.GERMAN_STANDARD]: { zero: 0, reduced: 7, standard: 19 },
		[GeneralRegulatoryMode.AUSTRIAN_STANDARD]: { zero: 0, reduced: 10, standard: 20 },
	};

	readonly DEFAULT_VAT_RATE = this.VAT_RATES[GeneralRegulatoryMode.GERMAN_STANDARD].standard;

	private readonly VAT_RATE_INTERMEDIATE = 12;

	readonly VAT_RATE_OPTIONS_ALL: VatRateOption[] = [
		{ value: 0, label: '0%', hint: 'billing.vatRateHintZero', modes: 'all' },
		{ value: 7, label: '7%', hint: 'billing.water', modes: [GeneralRegulatoryMode.GERMAN_STANDARD] },
		{ value: 10, label: '10%', hint: 'billing.water', modes: [GeneralRegulatoryMode.AUSTRIAN_STANDARD] },
		{ value: this.VAT_RATE_INTERMEDIATE, label: '12%', hint: '', modes: 'all' },
		{ value: 19, label: '19%', hint: 'common.default', modes: [GeneralRegulatoryMode.GERMAN_STANDARD] },
		{ value: 20, label: '20%', hint: 'common.default', modes: [GeneralRegulatoryMode.AUSTRIAN_STANDARD] },
	];

	// Mapping CostType -> VAT rate type (zero, reduced, standard)
	// Description will use translation key: 'enum.' + CostType
	readonly COST_TYPE_VAT_MAPPING: Record<CostType, 'zero' | 'reduced' | 'standard'> = {
		[CostType.ENERGY]: 'standard',
		[CostType.CHIMNEY_SWEEP]: 'standard',
		[CostType.OPERATING_ELECTRICITY]: 'standard',
		[CostType.COLD_AND_WASTEWATER]: 'reduced',
		[CostType.GARBAGE_COLLECTION]: 'standard',
		[CostType.CLEANING]: 'standard',
		[CostType.LIGHTING]: 'standard',
		[CostType.MAINTENANCE_COSTS]: 'standard',
		[CostType.INSURANCE]: 'standard',
		[CostType.TAXES]: 'zero',
		[CostType.RENTAL_DEVICE]: 'standard',
		[CostType.MAINTENANCE_DEVICE]: 'standard',
		[CostType.OTHER]: 'standard',
		[CostType.RESIDUAL_ELECTRICITY]: 'standard',
		[CostType.EXCESS_FEED_IN]: 'standard',
	};

	/**
	 * Returns the default VAT rate for the given regulatory mode.
	 */
	getDefaultVatRate(mode: GeneralRegulatoryMode): number {
		return this.VAT_RATES[mode].standard;
	}

	/**
	 * Retrieves the typical VAT rate associated with a specific cost type and regulatory mode.
	 */
	getTypicalVatRateForCostType(costType: CostType, mode: GeneralRegulatoryMode): number | undefined {
		const rateType = this.COST_TYPE_VAT_MAPPING[costType];
		if (!rateType) return undefined;
		return this.VAT_RATES[mode][rateType];
	}

	/**
	 * Returns the display label for the mat-select-trigger of a VAT rate select.
	 * Collects all hints from options matching the selected value and joins them with ", ".
	 * E.g. "7% - Water, from cost group" or "19% - Default"
	 */
	getVatRateTriggerLabel(selectedValue: number, options: VatRateOption[]): string {
		const matches = options.filter((o) => o.value === selectedValue);
		if (!matches.length) return '';
		const hints = matches
			.map((o) => o.hint)
			.filter((h) => !!h)
			.map((h) => this.translocoService.translate(h));
		return hints.length ? `${matches[0].label} - ${hints.join(', ')}` : matches[0].label;
	}

	/**
	 * Returns the VAT rate options for a given regulatory mode with an optional preferred VAT rate set on the first position.
	 */
	getVatRateOptions(mode: GeneralRegulatoryMode, preferredVatRate?: number | null): VatRateOption[] {
		const options = this.VAT_RATE_OPTIONS_ALL.filter((opt) => opt.modes === 'all' || opt.modes.includes(mode));

		if (preferredVatRate != null) {
			return [
				{
					value: preferredVatRate,
					label: `${preferredVatRate}%`,
					hint: this.translocoService.translate('billing.vatRateFromCostGroup'),
					modes: 'all',
				},
				...options,
			];
		}
		return options;
	}
}
